import { Box, Flex, FormControl, Input, Stack, Switch, Tag } from "@chakra-ui/react"
import { ErrorMessage, Formik } from "formik"
import { ComponentProps, FC, useState } from "react"
import ReactSelect from "react-select"
import { SelectOption } from "src/components/shared/ReactSelect"
import { Country } from "src/domain/entities/country"
import { IStateUpdateFormFields } from "."
import { State } from "../../../../domain/entities/state"
import { fetchGujaratiSuggestions } from "../../../../utils/helpers"
import { FormikOnSubmit } from "../../../../utils/types"
import { DrawerForm, ErrorMessageField } from "../../../ui"
import { InputLabel } from "../../../ui/InputLabel"

interface Props extends Omit<ComponentProps<typeof DrawerForm>, "children"> {
	state: State
	handleSubmit: FormikOnSubmit<IStateUpdateFormFields>
	countryList: Country[]
}

export const StateUpdateDrawerFormView: FC<Props> = ({
	state,
	handleSubmit,
	countryList,
	...rest
}) => {
	const [nameSuggestions, setNameSuggestions] = useState<string[]>([])

	return (
		<Formik<IStateUpdateFormFields>
			initialValues={{
				name: state.name,
				countryId: state.country?.id,
				isApproved: true,
			}}
			onSubmit={handleSubmit}
			enableReinitialize={true}
		>
			{({ values, isSubmitting, handleChange, setFieldValue }) => {
				const handleNameChange = async (
					e: React.ChangeEvent<HTMLInputElement>,
				) => {
					setFieldValue("name.en", e.target.value)
					const suggestions = await fetchGujaratiSuggestions(e.target.value)
					setNameSuggestions(suggestions)
				}

				const countryOptions = countryList.map((country) => ({
					value: country.id,
					label: country.name.en + ` (${country.name.gu})`,
				}))

				return (
					<DrawerForm
						size="sm"
						headerLabel="Update State"
						submitLabel="Save"
						isSubmitting={isSubmitting}
						{...rest}
					>
						<Stack maxWidth={"sm"} marginX={"auto"}>
							{/* Country */}
							<FormControl flex={2}>
								<InputLabel label="Country" />
								<ReactSelect
									name="countryId"
									onChange={(newValue) => {
										setFieldValue(
											"countryId",
											(newValue as SelectOption).value,
										)
									}}
									value={countryOptions.find(
										(el) => el.value === values.countryId,
									)}
									options={countryOptions}
									isSearchable
								/>
							</FormControl>
							<Flex gridGap={2}>
								<Box>
									{/* Name */}
									<FormControl>
										<InputLabel label="State Name" />
										<Input
											name="name.en"
											placeholder="Name"
											maxLength={50}
											required
											autoFocus
											value={values.name?.en}
											onChange={handleNameChange}
										/>
										<ErrorMessage
											component={ErrorMessageField}
											name="name.en"
										/>
									</FormControl>
									<Box>
										{nameSuggestions.map((el, i) => (
											<Tag
												colorScheme={"green"}
												backgroundColor={"green.50"}
												variant="outline"
												_hover={{
													backgroundColor: "green.100",
												}}
												cursor="pointer"
												margin={0.5}
												onClick={() => {
													setFieldValue("name.gu", el)
													setNameSuggestions([])
												}}
												key={i}
											>
												{el}
											</Tag>
										))}
									</Box>
								</Box>

								{/* Name Gu */}
								<FormControl>
									<InputLabel label="Name Gujarati" />
									<Input
										name="name.gu"
										placeholder="Name Gujarati"
										maxLength={50}
										required
										value={values.name?.gu}
										onChange={handleChange}
									/>
									<ErrorMessage
										component={ErrorMessageField}
										name="name.gu"
									/>
								</FormControl>
							</Flex>
							<FormControl>
								<InputLabel label="Approved?" />
								<Box>
									<Switch
										isChecked={values.isApproved}
										onChange={(val) => {
											setFieldValue(
												"isApproved",
												val.target.checked,
											)
										}}
									/>
								</Box>
							</FormControl>
						</Stack>
					</DrawerForm>
				)
			}}
		</Formik>
	)
}
